/**
 * Inline Video Module (formerly Floating Video)
 * @module floatingVideo
 */

import $ from 'jquery';
import './custom-video-player';
import lazyVideo from './lazy-video';
import registerJQueryPlugin from '../../lib/register-jquery-plugin';
import mergeOptions from '../../lib/merge-options';

// Expose the function as a jQuery plugin for ease of use
export const PLUGIN_NAME = 'floatingVideo';
export const DEFAULTS = {
  videoMuted: true,
  // Renders the mute/volume button (flag is inverted). Defaulted here so the
  // gallery gets it regardless of which init path runs — one caller invokes
  // .floatingVideo() with no options. [UX-20630]
  hideMute: true,
  // Wait until the trigger is visible before building the video, so a video
  // in a hidden tab (Size & Fit) downloads nothing on page load. [UX-21437]
  deferUntilVisible: false,
  $originalTrigger: null
};

// Source is deferred (data-src + preload="none") — see lazy-video.js. [UX-21388]
const buildVideo = (videoUrl, videoPosterUrl, aspectRatio) => $(`
  <video class="u-full-width" ${aspectRatio ? `style="aspect-ratio:${aspectRatio};"` : ''} poster="${videoPosterUrl}" preload="none" playsinline="playsinline" loop muted>
    <source data-src="${videoUrl}" type="video/mp4">
  </video>
`);

/**
 * Replaces the trigger's thumbnail with an inline video and wires it up to
 * the custom video player. The video adopts the placeholder image's aspect
 * ratio so its height matches surrounding gallery image slides.
 * @param  {jQuery} $el - trigger element
 * @param  {Object} options - Any options provided upon initialization
 */
const renderInlineVideo = ($el, options) => {
  const videoUrl = $el.data('videoUrl');
  const videoPosterUrl = $el.data('videoPosterUrl');
  const $thumbnail = $el.find('figure');
  const $img = $thumbnail.find('img');

  let aspectRatio;
  if ($img.length) {
    const w = $img.attr('width');
    const h = $img.attr('height');
    if (w && h) aspectRatio = `${w}/${h}`;
  }

  const $video = buildVideo(videoUrl, videoPosterUrl, aspectRatio);

  if ($thumbnail.length) {
    $thumbnail.replaceWith($video);
  } else {
    $el.prepend($video);
  }

  $video.customVideoPlayer(options);
  lazyVideo($video[0]);
};

/**
 * Initializes floatingVideo
 * @param {HTMLElement} el - trigger element with data-video-url and data-video-poster-url
 * @param {Object} options - Options used for wiring up the video
 * @see DEFAULTS
 */
export default function floatingVideo(el, options = {}) {
  const $el = $(el);
  // Already rendered (e.g. re-run after the details markup is ajaxed in) —
  // a second pass would prepend a duplicate video. [UX-21388]
  if ($el.find('video').length) return {};

  const opts = mergeOptions(DEFAULTS, options, $el);
  opts.$originalTrigger = $el;

  if (opts.deferUntilVisible && 'IntersectionObserver' in window) {
    const observer = new IntersectionObserver((entries) => {
      if (entries.some((entry) => entry.isIntersecting)) {
        observer.disconnect();
        renderInlineVideo($el, opts);
      }
    }, { rootMargin: '200px 0px' });
    observer.observe($el[0]);
    return {};
  }

  renderInlineVideo($el, opts);

  return {};
}
registerJQueryPlugin(PLUGIN_NAME, floatingVideo);


// WEBPACK FOOTER //
// ./styleguide/src/assets/modules/abtest/floating-video.js